import { useMemo } from 'react';
import { AreaChart, ResponsiveContainer, YAxis } from 'recharts';
import type { ChartGranularity } from '@/lib/chart-axis';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import { markPartialRows, type ChartRange } from '@/lib/partial-buckets';
import { MetricsChartAreaSeries } from '@/components/ui/charts/chart-area-series';
import { cn } from '@/lib/utils';

export type SparklineRow = {
  /** X category (bucket label). */
  month: string;
  /** Bucket start (api date), for the partial detection. */
  date: string;
  value: number;
};

export interface HomeSparklineProps {
  rows: SparklineRow[];
  range: ChartRange;
  granularity: ChartGranularity;
  /** Size of the box (default `h-8 w-full`, the Início tile). */
  className?: string;
}

/**
 * Minigráfico of an Início tile (HOME-02): the period's line without axes,
 * the partial first/last bucket dashed with a hollow ring (CHART-02), no
 * label next to it (the tile is too small).
 */
export function HomeSparkline({
  rows,
  range,
  granularity,
  className,
}: HomeSparklineProps) {
  const partial = usePartialBuckets(rows, range, granularity);
  const data = useMemo(
    () => markPartialRows(rows, 'value', partial.edges),
    [rows, partial.edges]
  );

  return (
    <div
      aria-hidden="true"
      className={cn('h-8 w-full', className)}
      data-home-sparkline
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 4, right: 5, bottom: 4, left: 5 }}
        >
          <YAxis hide domain={[0, 'auto']} />
          <MetricsChartAreaSeries
            dataKey="value"
            partialDataKey="valuePartial"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
